import { PERMISSIONS } from './constants/permissions';
const {
  ACCESS_COMPANY_MAILBOX,
  ACCESS_DOCUMENT_TEMPLATES,
  ACCESS_MAILBOX,
  ACCESS_QUESTIONNAIRES,
  ADD_APPLICATION_NOTE,
  ADD_CANDIDATE_NOTE,
  ADD_JOB_NOTE,
  ADD_USERS_TO_CHILD_COMPANY,
  BILLING_FULL_ACCESS,
  COPY_APPLICATION,
  CREATE_CHILD_COMPANY,
  CREATE_ACCOUNT,
  CREATE_CV,
  CREATE_DEPARTMENT,
  CREATE_FORM,
  CREATE_INTERVIEW_SLOT,
  CREATE_UPDATE_JOB,
  CREATE_LICENSE_REQUEST,
  CREATE_TEMPLATE,
  DECLINE_APPLICATION,
  MANAGE_CANDIDATES,
  MANAGE_CANDIDATE_SOURCE,
  MANAGE_E_SIGNER,
  MANAGE_CAREERSITE,
  MANAGE_TAGS,
  MANAGE_TALENT_POOLS,
  MANAGE_SKILLS,
  MANAGE_PIPELINE_TEMPLATES,
  MANAGE_APPROVERS,
  MANAGE_COMPANY_PROFILE,
  MANAGE_REPORT_ANALYTICS,
  MANAGE_API_HUB,
  MANAGE_SLOTS,
  MANAGE_COPYWRITE,
  DELETE_DEPARTMENT,
  DELETE_ACCOUNT,
  DELETE_JOB,
  EXTEND_JOB_EXPIRY,
  EXTERNAL_CALENDAR_CRUD,
  HIRING_PLANS_APPROVE,
  MANAGE_DECLINE_REASONS,
  MANAGE_DOCUMENTS,
  MANAGE_EVENTS,
  MANAGE_FORMS,
  MANAGE_APPLICATION_FORMS,
  MANAGE_APPLICATIONS,
  MANAGE_INTERVIEWS,
  MANAGE_JOB_STAGES,
  MANAGE_RETENTION_POLICIES,
  MANAGE_ROLES,
  MANAGE_TEMPLATES,
  MANAGE_WORKING_HOURS,
  MANAGE_VIDEO_PROFILES,
  RECEIVE_INTERVIEW_REMINDERS,
  READ_DASHBOARD_TIMELINE,
  SEARCH_CV,
  SEND_EMAIL,
  SOCIAL_MEDIA_CRUD,
  SOCIAL_MEDIA_POSTING,
  SUBSCRIPTION_READ_ACCESS,
  TRANSFER_RESPONSIBILITIES,
  UPDATE_CHILD_COMPANY,
  UPDATE_ACCOUNT,
  UPDATE_DEPARTMENT,
  VIEW_ALL_ACCOUNTS,
  VIEW_ALL_CANDIDATES,
  VIEW_ALL_CVS,
  VIEW_ALL_JOBS,
  VIEW_DOCUMENT_TEMPLATES,
  VIEW_FORMS,
  VIEW_PAID_CVS,
  VIEW_VIDEO_PROFILES,
} = PERMISSIONS;

/** Ordered: first match wins. More-specific patterns before generic ones. */

const e = (methods, pattern) => ({
  methods: Array.isArray(methods) ? methods : [methods],
  pattern,
});

/**
 * Optimized source of truth:
 * - Permission appears once per group (`permission`) or use `permissions` for OR checks.
 * - Same route under same permission can hold multiple methods in one entry.
 */
const ROUTE_PERMISSION_GROUPS = [
  {
    permission: MANAGE_WORKING_HOURS,
    entries: [
    e(['GET', 'POST', 'PUT'], 'accounts/working-hours'),
    ],
  },
  {
    permission: DELETE_ACCOUNT,
    entries: [
    e('PUT', 'accounts/bulk-delete'),
    e('DELETE', 'accounts'),
    ],
  },
  {
    // Ungated on backend; must precede accounts/:id so literal segments are not swallowed by :id.
    entries: [
    e(['GET', 'PUT'], 'accounts/availability'),
    e('GET', 'accounts/availability-slots'),
    e(['GET', 'PUT'], 'accounts/wizard-details'),
    ],
  },
  {
    permission: UPDATE_ACCOUNT,
    entries: [
    e('PUT', 'accounts/:id'),
    ],
  },
  {
    permission: CREATE_ACCOUNT,
    entries: [
    e('GET', 'accounts/:id/resend-invite'),
    ],
  },
  {
    permission: VIEW_ALL_ACCOUNTS,
    entries: [
    //e('GET', 'accounts'),
    e('GET', 'accounts/details'),
    ],
  },
  {
    permission: MANAGE_INTERVIEWS,
    entries: [
    e('GET', 'applications/:id/slots'),
    e('GET', 'applications/:appId/slots/:interviewId'),
    e('PUT', 'applications/:id/slots/:appSlotId/:attendanceStatus'),
    e('POST', 'interviews'),
    e(['DELETE', 'GET', 'PUT'], 'interviews/:interviewId'),
    e('PUT', 'interviews/:interviewId/accept'),
    e('PUT', 'interviews/:interviewId/reject'),
    e('POST', 'interviews/invitation'),
    e('GET', 'interviews/ooo-interviews'),
    ],
  },
  {
    permission: EXTERNAL_CALENDAR_CRUD,
    entries: [
    e('GET', 'calendar/providers'),
    e('GET', 'calendar/providers/:provider/login'),
    e('GET', 'calendar/providers/:provider/login-info'),
    e('DELETE', 'calendar/providers/:provider/logout'),
    e('GET', 'calendar/providers/:provider/sync'),
    e('GET', 'calendar/providers/callback'),
    e('GET', 'calendar/providers/connected'),
    ],
  },
  {
    permission: SOCIAL_MEDIA_CRUD,
    entries: [
    e('GET', 'social-media/settings/products'),
    e('GET', 'social-media/accounts'),
    e('GET', 'social-media/facebook/connect'),
    e('DELETE', 'social-media/facebook/connection'),
    e(['GET', 'POST'], 'social-media/facebook/pages'),
    e('DELETE', 'social-media/facebook/pages/:id'),
    e('GET', 'social-media/instagram/connect'),
    e('DELETE', 'social-media/instagram/connection'),
    e(['GET', 'POST'], 'social-media/instagram/accounts'),
    e('DELETE', 'social-media/instagram/accounts/:id'),
    ],
  },
  {
    permission: SOCIAL_MEDIA_POSTING,
    entries: [
    e('GET', 'social-media/posting/products'),
    ],
  },
  {
    permission: ADD_USERS_TO_CHILD_COMPANY,
    entries: [
    e('POST', 'company/:companyId/add-users'),
    ],
  },
  {
    permission: UPDATE_CHILD_COMPANY,
    entries: [
    e('PUT', 'company/:companyId/update'),
    ],
  },
  {
    permission: VIEW_ALL_ACCOUNTS,
    entries: [
    e('GET', 'company/accounts-count'),
    ],
  },
  {
    permission: CREATE_CHILD_COMPANY,
    entries: [
    e('POST', 'company/add-company'),
    ],
  },
  {
    permission: MANAGE_RETENTION_POLICIES,
    entries: [
    e('GET', 'company/data-retention'),
    e('POST', 'company/data-retention'),
    ],
  },
  {
    permission: ACCESS_COMPANY_MAILBOX,
    entries: [
    e(['GET', 'POST'], 'company/mail'),
    e(['DELETE', 'GET'], 'company/mail/:id'),
    ],
  },
  {
    permission: CREATE_CV,
    entries: [
    e('POST', 'cvlib/candidate'),
    ],
  },
  {
    permission: VIEW_ALL_CVS,
    entries: [
    e('GET', 'cvlib/candidate/:recordId'),
    e('POST', 'cvlib/candidates'),
    ],
  },
  {
    permission: SEARCH_CV,
    entries: [
    e('POST', 'cvlib/candidates/search'),
    ],
  },
  {
    permission: VIEW_PAID_CVS,
    entries: [
    e('GET', 'cvlib/purchased/candidates'),
    ],
  },
  {
    permission: VIEW_ALL_CANDIDATES,
    entries: [
    e('GET', 'candidates/list'),
    ],
  },
  {
    permission: READ_DASHBOARD_TIMELINE,
    entries: [
    e('GET', 'dashboard-timeline'),
    ],
  },
  {
    permission: MANAGE_DECLINE_REASONS,
    entries: [
    e('POST', 'decline-reasons'),
    e(['DELETE', 'PUT'], 'decline-reasons/:id'),
    ],
  },
  {
    permission: CREATE_DEPARTMENT,
    entries: [
    e('POST', 'departments'),
    ],
  },
  {
    permission: MANAGE_CANDIDATES,
    entries: [
    e('DELETE', 'candidates/:id'),
    e('PUT', 'candidates/:id'),
    e('POST', 'candidates/:id/assign'),
    e('POST', 'candidates/:id/document'),
    e('DELETE', 'candidates/:id/document/:docId'),
    e('POST', 'candidates/manual-dashboard-form'),
    e('POST', 'candidates/data-base'),
    e('POST', 'candidates/bulk/queue/retry'),
    e('POST', 'candidates/bulk/queue/:jobId/:jhpId'),
    e('POST', 'candidate-talent-pool/pools/candidates'),
    e('PUT', 'candidate-talent-pool/pools/candidates/bulk-delete'),
    ],
  },
  {
    permission: MANAGE_TALENT_POOLS,
    entries: [
    e('POST', 'candidate-talent-pool/pools'),
    e('PUT', 'candidate-talent-pool/pools/:id'),
    e('DELETE', 'candidate-talent-pool/pools/:id'),
    ],
  },
  {
    permission: ADD_CANDIDATE_NOTE,
    entries: [
    e('POST', 'candidates/notes'),
    ],
  },
  {
    permission: DELETE_DEPARTMENT,
    entries: [
    e('DELETE', 'departments/:id'),
    ],
  },
  {
    permission: UPDATE_DEPARTMENT,
    entries: [
    e('PUT', 'departments/:id'),
    ],
  },
  {
    permission: MANAGE_DOCUMENTS,
    entries: [
    e(['POST', 'PUT'], 'documents'),
    e('DELETE', 'documents/:id'),
    ],
  },
  {
    permission: MANAGE_EVENTS,
    entries: [
    e(['GET', 'POST'], 'events'),
    e(['DELETE', 'GET', 'PUT'], 'events/:id'),
    ],
  },
  {
    permission: ACCESS_QUESTIONNAIRES,
    entries: [
    //e('GET', 'forms'),
    // e('POST', 'forms/questionnaire-form'),
    e('DELETE', 'forms/questionnaire-form/:id'),
    // e('GET', 'jobs/:jobId/questionnaire-forms'),
    ],
  },
  {
    permission: CREATE_FORM,
    entries: [
    e('POST', 'forms'),
    e('POST', 'forms/duplicate'),
    e('POST', 'forms/templates/:key'),
    ],
  },
  {
    permission: MANAGE_FORMS,
    entries: [
    e(['DELETE', 'PUT'], 'forms/:id'),
    e('PUT', 'forms/:id/accepting-responses'),
    e('PUT', 'forms/custom-form/attributes'),
    ],
  },
  {
    permission: VIEW_FORMS,
    entries: [
    e('GET', 'forms/templates'),
    e('GET', 'forms/templates/:key'),
    e('GET', 'forms/:id/custom-form'),
    e('GET', 'forms/:id/overview'),
    e('POST', 'forms/app-questionnaire-form/submit'),
    e('GET', 'forms/dei/form'),
    e('GET', 'forms/status'),
    e('GET', 'jobs/:jobId/questionnaire-forms/:applicationId'),
    e('GET', 'jobs/:jobId/questionnaire-forms/by-users/:applicationId'),
    ],
  },
  {
    permission: ADD_JOB_NOTE,
    entries: [
    e('POST', 'jobs/notes'),
    e('POST', 'jobs/notes/preSignedNoteAttachment'),
    e('PUT', 'jobs/notes'),
    ],
  },
  {
    permission: CREATE_UPDATE_JOB,
    entries: [
    e('PUT', 'jobs/v2/:jobId'),
    e('PUT', 'jobs/v2/:jobId/:jhpId'),
    e('PUT', 'hiring-plans'),
    e('PUT', 'jobs/:jobId'),
    e('POST', 'jobs/:jobId/hiring-stages'),
    e('PUT', 'jobs/:jobId/title'),
    e('POST', 'hiring-plans/accounts'),
    e('POST', 'hiring-stages'),
    e('POST', 'job-accounts'),
    e('POST', 'jobs'),
    e('POST', 'jobs/v2'),
    e('POST', 'stages/jobs-hiring-stage/trigger'),
    e('PUT', 'hiring-stages/:stageId'),
    e('PUT', 'jobs/:jobId/hiring-stages/:stageId'),
    ],
  },
  {
    permission: EXTEND_JOB_EXPIRY,
    entries: [
    e('PUT', 'hiring-plans/extend-request/:id'),
    ],
  },
  {
    permission: HIRING_PLANS_APPROVE,
    entries: [
    e('GET', 'hiring-plans/approver'),
    e('POST', 'hiring-plans/approver/approve-reject'),
    ],
  },
  {
    permission: VIEW_ALL_JOBS,
    entries: [
    e('GET', 'hiring-stages'),
    e('GET', 'job-accounts/:jobId'),
    e('GET', 'jobs/:jobId/hiring-stages/:stageId'),
    e('GET', 'jobs/dashboard/live-activity/:jobId'),
    ],
  },
  {
    permission: DELETE_JOB,
    entries: [
    e('DELETE', 'hiring-stages/:stageId'),
    e('DELETE', 'jobs/:jobId'),
    e('DELETE', 'jobs/:jobId/draft'),
    e('DELETE', 'jobs/:jobId/hiring-stages/:stageId'),
    e('DELETE', 'jobs/notes/:id/:jobId'),
    e('DELETE', 'template/job/:id'),
    ],
  },
  {
    permission: RECEIVE_INTERVIEW_REMINDERS,
    entries: [
    e('POST', 'interviews/reminder'),
    e('PUT', 'interviews/reminder/:id'),
    ],
  },
  {
    permission: CREATE_INTERVIEW_SLOT,
    entries: [
    e('POST', 'interviews/slot/preSignedMailAttachment'),
    ],
  },
  {
    permission: VIEW_DOCUMENT_TEMPLATES,
    entries: [
    e('GET', 'jobOffers/jobOffersTemplate'),
    ],
  },
  {
    permission: ACCESS_DOCUMENT_TEMPLATES,
    entries: [
    e(['POST', 'PUT'], 'jobOffers/jobOffersTemplate'),
    e(['DELETE', 'GET'], 'jobOffers/jobOffersTemplate/:id'),
    ],
  },
  {
    permission: CREATE_TEMPLATE,
    entries: [
    e('POST', 'templates'),
    ],
  },
  {
    permission: MANAGE_TEMPLATES,
    entries: [
    e('PUT', 'templates/:id'),
    e('DELETE', 'templates/:id'),
    ],
  },
  {
    permissions: [CREATE_UPDATE_JOB, MANAGE_JOB_STAGES],
    entries: [
    e('PUT', 'jobs/:jobId/order/hiring-stages'),
    ],
  },
  {
    permission: ADD_APPLICATION_NOTE,
    entries: [
    e('POST', 'applications/notes'),
    e('DELETE', 'applications/notes/:id/:candidateId/:applicationId'),
    ],
  },
  {
    permission: ADD_CANDIDATE_NOTE,
    entries: [
    e('POST', 'candidates/notes'),
    ],
  },
  {
    permission: DECLINE_APPLICATION,
    entries: [
    e('PUT', 'applications/declined'),
    ],
  },
  {
    permission: COPY_APPLICATION,
    entries: [
    e('POST', 'applications/:id/assign'),
    ],
  },
  {
    permission: ACCESS_MAILBOX,
    entries: [
    e('GET', 'mail'),
    e('GET', 'mail/:id'),
    e('GET', 'mail/unRead'),
    ],
  },
  {
    permission: SEND_EMAIL,
    entries: [
    e('POST', 'mail'),
    e('DELETE', 'mail/:id'),
    e('DELETE', 'mail/attachment'),
    e('POST', 'mail/preSignedMailAttachment'),
    ],
  },
  {
    permission: TRANSFER_RESPONSIBILITIES,
    entries: [
    e('GET', 'responsibilities/assigned/tasks'),
    e('GET', 'responsibilities/pending/tasks'),
    e('GET', 'responsibilities/tasks/:id'),
    e('POST', 'responsibilities/transfer/tasks'),
    e('DELETE', 'responsibilities/user/:id'),
    e('GET', 'responsibilities/users/role-base'),
    ],
  },
  {
    permission: BILLING_FULL_ACCESS,
    entries: [
    e('POST', 'subscription/auto-renew'),
    e('POST', 'subscription/charge/value-pack'),
    e('POST', 'subscription/credit-request'),
    e('POST', 'subscription/reactive/prev-pkg'),
    e('DELETE', 'subscription/switch/:companyProductPackageId'),
    e('POST', 'subscription/transfer-credits'),
    ],
  },
  {
    permission: SUBSCRIPTION_READ_ACCESS,
    entries: [
    e('GET', 'subscription/feature-products'),
    e('GET', 'subscription/license-details'),
    ],
  },
  {
    permission: CREATE_LICENSE_REQUEST,
    entries: [
    e('POST', 'subscription/license-renew'),
    ],
  },
  {
    permission: MANAGE_APPLICATIONS,
    entries: [
    e('GET', 'applications'),
    e('GET', 'applications/apps'),
    e('GET', 'applications/:id'),
    e('GET', 'applications/:id/jobs'),
    e('GET', 'applications/:id/documents'),
    e('GET', 'applications/:id/sms'),
    e('GET', 'applications/:id/video-profiles'),
    e('GET', 'applications/messages'),
    e('GET', 'applications/declined-count/:jobId'),
    e('GET', 'applications/notes'),
    e('GET', 'applications/notes/:id/:applicationId/:candidateId'),
    e('GET', 'applications/:appId/references'),
    e('GET', 'applications/:appId/skills'),
    e('GET', 'applications/:appId/skills/all'),
    e('DELETE', 'applications/:id'),
    e('POST', 'applications/:id/documents'),
    e('DELETE', 'applications/:id/document/:docId'),
    e('PUT', 'applications/change-stages'),
    e('PUT', 'applications/jhp'),
    e('PUT', 'applications/status/:statusId'),
    e('POST', 'applications/contact'),
    e('POST', 'applications/mail'),
    e('POST', 'applications/sms'),
    e('POST', 'applications/trigger-stages-notifications'),
    ],
  },
  {
    permission: MANAGE_APPLICATION_FORMS,
    entries: [
    e('POST', 'applications/:appId/skills'),
    e('POST', 'applications/skills/average'),
    ],
  },
  {
    permission: VIEW_VIDEO_PROFILES,
    entries: [
    e('GET', 'video-profiles'),
    ],
  },
  {
    permission: MANAGE_VIDEO_PROFILES,
    entries: [
    e('POST', 'video-profiles'),
    e(['DELETE', 'PUT'], 'video-profiles/:id'),
    ],
  },
  {
    permission: MANAGE_ROLES,
    entries: [
    e('GET', 'permissions'),
    e('GET', 'company-roles'),
    e('POST', 'company-roles'),
    e('PUT', 'company-roles/:id'),
    e('DELETE', 'company-roles/:id'),
    e('GET', 'company-roles/:compRoleId/permissions'),
    e('POST', 'company-roles/:compRoleId/permissions'),
    e('DELETE', 'company-roles/:compRoleId/permissions'),
    ],
  },
  {
    permission: MANAGE_CANDIDATE_SOURCE,
    entries: [
    e('POST', 'products/apply-sources'),
    e('PUT', 'products/apply-sources/:id'),
    e('DELETE', 'products/apply-sources/:id'),
    ],
  },
  {
    permission: MANAGE_E_SIGNER,
    entries: [
    e('POST', 'e-signee'),
    e('PUT', 'e-signee/:id'),
    e('DELETE', 'e-signee/:id'),
    ],
  },
  {
    permission: MANAGE_CAREERSITE,
    entries: [
    e('POST', 'careersite/preSignedURL'),
    e(['GET', 'POST'], 'careersite/settings'),
    e('POST', 'careersite/company/page'),
    e('GET', 'careersite/company/pages'),
    e(['DELETE', 'GET', 'PUT'], 'careersite/company/page/:id'),
    e('PUT', 'careersite/company/page/:id/name'),
    e('GET', 'careersite/components'),
    e('GET', 'careersite/component/:id'),
    e('POST', 'careersite/company/upload-media'),
    e('POST', 'careersite/extDomain'),
    e('PUT', 'careersite/extDomain/:id'),
    e('GET', 'careersite/verify/extDomain'),
    e('GET', 'careersite/custom-themes'),
    e('POST', 'careersite/custom-theme'),
    e('DELETE', 'careersite/custom-theme/:id'),
    e('POST', 'careersite/publish'),
    e('PUT', 'careersite/company/settings'),
    ],
  },
  {
    permission: MANAGE_TAGS,
    entries: [
    e('POST', 'tags'),
    e('PUT', 'tags/:tagId'),
    e('DELETE', 'tags/:tagId'),
    ],
  },
  {
    permission: MANAGE_SKILLS,
    entries: [
    e('POST', 'skills'),
    e('PUT', 'skills/:skillId'),
    e('DELETE', 'skills/:skillId'),
    ],
  },
  {
    permission: MANAGE_PIPELINE_TEMPLATES,
    entries: [
    e('POST', 'stages/pipeline-templates'),
    e('PUT', 'stages/pipeline-templates'),
    e('DELETE', 'stages/pipeline-templates/:templateId'),
    e('PUT', 'stages/pipeline-template/:templateId/info'),
    e('POST', 'tpl-automations/stage-events'),
    e('PUT', 'tpl-automations/stage-event/:autoStageEventId'),
    e('DELETE', 'tpl-automations/stage-events'),
    ],
  },
  {
    permission: MANAGE_APPROVERS,
    entries: [
    e('POST', 'approvers'),
    e('PUT', 'approvers/:id'),
    e('DELETE', 'approvers/:id'),
    ],
  },
  {
    permission: MANAGE_COMPANY_PROFILE,
    entries: [
    e('PUT', 'company'),
    e('PUT', 'company/ai-score-weights'),
    e('POST', 'company/ai-score-weights/reset'),
    e('POST', 'company/preSignedCompProfileImage'),
    e('POST', 'company/benefit'),
    e('PUT', 'company/benefit/:id'),
    e('DELETE', 'company/benefit/:id'),
    ],
  },
  {
    permission: MANAGE_REPORT_ANALYTICS,
    entries: [
    e('GET', 'reports/overview-filters-data'),
    e('GET', 'reports/overview-summary'),
    e('GET', 'reports/overview-pipeline'),
    e('GET', 'reports/overview-hiring-trend'),
    e('GET', 'reports/jobs/section/group-count'),
    e('GET', 'reports/jobs/section/overtime'),
    e('GET', 'reports/candidates/section/app-by-channel'),
    e('GET', 'reports/candidates/section/by-location'),
    e('GET', 'reports/candidates/section/summary'),
    e('GET', 'reports/candidates/section/channel-affectiveness'),
    e('GET', 'reports/dei/section/form-status'),
    e('GET', 'reports/dei/section/candidate/pool-diversity'),
    e('GET', 'reports/dei/section/pipeline-diversity'),
    e('GET', 'reports/dei/section/offer-base/group'),
    e('GET', 'reports/dei/section/offers-status'),
    e('GET', 'reports/dei/section/download'),
    e('GET', 'reports/key-performance-metrics'),
    e('GET', 'reports/key-performance-metrics/candidate-pool'),
    e('GET', 'reports/overview/campaign-performance'),
    e('GET', 'reports/overview/hp-progression'),
    e('GET', 'reports/overview/hiring-efficiency'),
    e('GET', 'reports/jobs/hiring-trend'),
    e('GET', 'reports/jobs/job-analysis'),
    e('GET', 'reports/jobs/copywritten'),
    e('GET', 'reports/jobs/interviews-scheduled'),
    e('GET', 'reports/campaigns/campaign-analysis'),
    e('GET', 'reports/campaigns/campaign-performance'),
    e('GET', 'reports/campaigns/candidate-analysis'),
    e('GET', 'reports/candidates/location'),
    e('GET', 'reports/candidates/rejection-reason'),
    e('GET', 'reports/candidates/candidate-analysis'),
    e('GET', 'reports/download'),
    e('GET', 'reports/download/job-data'),
    e('GET', 'reports/download/applicant-data'),
    e('GET', 'superset/config'),
    e('POST', 'superset/guest-token'),
    e('GET', 'superset/dashboards'),
    e('GET', 'superset/charts'),
    e('GET', 'superset/scheduled-reports'),
    e('GET', 'superset/scheduled-reports/:reportId'),
    e('GET', 'superset/scheduled-reports/:reportId/logs'),
    e('POST', 'superset/scheduled-reports'),
    e('PUT', 'superset/scheduled-reports/:reportId'),
    e('DELETE', 'superset/scheduled-reports/:reportId'),
    e('GET', 'superset/related/:columnName'),
    e('GET', 'superset/dashboards/:dashboardId/tabs'),
    ],
  },
  {
    permission: MANAGE_API_HUB,
    entries: [
    e(['GET', 'POST'], 'keys'),
    e(['PUT', 'DELETE'], 'keys/:id'),
    ],
  },
  {
    permission: MANAGE_SLOTS,
    entries: [
    e('GET', 'interviews/slots'),
    ],
  },
  {
    permission: MANAGE_COPYWRITE,
    entries: [
    e('GET', 'copy-write'),
    e('GET', 'copy-write/:jobId'),
    e('POST', 'copy-write/accepted'),
    e('POST', 'copy-write/requestRevision'),
    ],
  },
];

/**
 * Routes intentionally not bound to a permission key yet (from route-permissions.md).
 * Kept here so all API routes are tracked in one module.
 */
export const ROUTES_WITHOUT_PERMISSION = [
  { method: 'POST', pattern: 'advertise' },
  { method: 'POST', pattern: 'ai/candidate-story' },
  { method: 'POST', pattern: 'ai/candidate-summary' },
  { method: 'POST', pattern: 'ai/generate-context-tpl' },
  { method: 'POST', pattern: 'ai/generate-tpl' },
  { method: 'GET', pattern: 'ai/job/:jobId/skill-set' },
  { method: 'POST', pattern: 'ai/job/:jobType' },
  { method: 'GET', pattern: 'ai/job/templates' },
  { method: 'GET', pattern: 'ai/job/tpl-details' },
  { method: 'GET', pattern: 'analytics/jobs' },
  { method: 'GET', pattern: 'authority-to-recruit/approval-chains' },
  { method: 'POST', pattern: 'authority-to-recruit/approval-chains' },
  { method: 'GET', pattern: 'authority-to-recruit/approval-chains/:id' },
  { method: 'PUT', pattern: 'authority-to-recruit/approval-chains/:id' },
  { method: 'DELETE', pattern: 'authority-to-recruit/approval-chains/:id' },
  { method: 'GET', pattern: 'authority-to-recruit/approval-chains/approvers' },
  { method: 'GET', pattern: 'authority-to-recruit/reference-data' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/assigned' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/stats/my-requests' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/stats/assigned' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/eligible-campaign-jobs' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/:id' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/action' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/withdraw' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/resubmit' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/amend-resubmit' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/complete' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/notify-approver' },
  { method: 'POST', pattern: 'applicant-employment-agreement' },
  { method: 'GET', pattern: 'applicant-employment-agreement/envelope/details' },
  { method: 'POST', pattern: 'applicant-employment-agreement/invitation' },
  { method: 'GET', pattern: 'approvers' },
  { method: 'GET', pattern: 'approvers/:id' },
  { method: 'GET', pattern: 'auth/2fa/status' },
  { method: 'POST', pattern: 'auth/claim-grant' },
  { method: 'POST', pattern: 'auth/confirm-forgot' },
  { method: 'DELETE', pattern: 'auth/disable-mfa/:passCode' },
  { method: 'POST', pattern: 'auth/enable-mfa' },
  { method: 'POST', pattern: 'auth/forgot' },
  { method: 'POST', pattern: 'auth/log-in' },
  { method: 'GET', pattern: 'auth/otp' },
  { method: 'GET', pattern: 'auth/qr-code' },
  { method: 'POST', pattern: 'auth/refresh-auth' },
  { method: 'POST', pattern: 'auth/register-number' },
  { method: 'POST', pattern: 'auth/reset-password' },
  { method: 'POST', pattern: 'authority-to-recruit/approval-chains' },
  { method: 'PUT', pattern: 'authority-to-recruit/approval-chains/:id' },
  { method: 'DELETE', pattern: 'authority-to-recruit/approval-chains/:id' },
  { method: 'GET', pattern: 'authority-to-recruit/approval-chains/approvers' },
  { method: 'POST', pattern: 'authority-to-recruit/approval-chains/approvers' },
  { method: 'GET', pattern: 'authority-to-recruit/reference-data' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/assigned' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/stats/my-requests' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/stats/assigned' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/eligible-campaign-jobs' },
  { method: 'GET', pattern: 'authority-to-recruit/recruitment-requests/:id' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/action' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/withdraw' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/resubmit' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/amend-resubmit' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/complete' },
  { method: 'POST', pattern: 'authority-to-recruit/recruitment-requests/:id/resend-notification' },
  { method: 'GET', pattern: 'automations/events' },
  { method: 'GET', pattern: 'automations/stage-event/:autoStageEventId' },
  { method: 'DELETE', pattern: 'automations/stage-event/:autoStageEventId/remove' },
  { method: 'POST', pattern: 'job-automations/stage-events' },
  { method: 'PUT', pattern: 'job-automations/stage-event/:autoStageEventId' },
  { method: 'DELETE', pattern: 'job-automations/stage-events' },
  { method: 'GET', pattern: 'calendar/google-callback-url' },
  { method: 'GET', pattern: 'calendar/outlook-callback-url' },
  { method: 'GET', pattern: 'campaigns' },
  { method: 'POST', pattern: 'campaigns' },
  { method: 'PUT', pattern: 'campaigns/:campaignId/title' },
  { method: 'GET', pattern: 'candidate-account' },
  { method: 'PUT', pattern: 'candidate-account' },
  { method: 'GET', pattern: 'candidate-account/applications' },
  { method: 'GET', pattern: 'candidate-account/details' },
  { method: 'GET', pattern: 'candidate-account/filters/applications' },
  { method: 'POST', pattern: 'candidate-account/forgot-password' },
  { method: 'POST', pattern: 'candidate-account/gdpr-delete' },
  { method: 'POST', pattern: 'candidate-account/login' },
  { method: 'GET', pattern: 'candidate-account/manage/applications' },
  { method: 'POST', pattern: 'candidate-account/password' },
  { method: 'POST', pattern: 'candidate-account/preSignedProfileImage' },
  { method: 'POST', pattern: 'candidate-account/reset-password' },
  { method: 'GET', pattern: 'candidate-account/states' },
  { method: 'GET', pattern: 'candidates/:candId/skills' },
  { method: 'GET', pattern: 'candidates/:candId/skills/all' },
  { method: 'GET', pattern: 'candidates/:id' },
  { method: 'GET', pattern: 'candidates/:id/documents' },
  { method: 'GET', pattern: 'candidates/:id/messages' },
  { method: 'GET', pattern: 'candidates/:id/messages' },
  { method: 'GET', pattern: 'candidates/:id/sms' },
  { method: 'GET', pattern: 'candidates/:id/sms' },
  { method: 'GET', pattern: 'candidates/bulk/queue/:jobId' },
  { method: 'POST', pattern: 'candidates/mail' },
  { method: 'POST', pattern: 'candidates/presigned-appcand-doc' },
  { method: 'POST', pattern: 'candidates/presigned-doc' },
  { method: 'GET', pattern: 'candidates/quick-filter' },
  { method: 'POST', pattern: 'candidates/quick-filter' },
  { method: 'DELETE', pattern: 'candidates/quick-filter/:filterId' },
  { method: 'GET', pattern: 'candidates/:candidateId/pools' },
  { method: 'GET', pattern: 'candidate-talent-pool/pools' },
  { method: 'GET', pattern: 'candidate-talent-pool/pools/:poolId/matrix' },
  { method: 'GET', pattern: 'checks' },
  { method: 'GET', pattern: 'checks/:applicationId' },
  { method: 'POST', pattern: 'checks/background' },
  { method: 'GET', pattern: 'checks/question-profiles' },
  { method: 'POST', pattern: 'checks/ref-hook' },
  { method: 'POST', pattern: 'checks/reference' },
  { method: 'GET', pattern: 'checks/reference-linked-forms' },
  { method: 'POST', pattern: 'checks/reference/reminder' },
  { method: 'POST', pattern: 'checks/reference/send' },
  { method: 'GET', pattern: 'client/app' },
  { method: 'GET', pattern: 'client/app/:id' },
  { method: 'GET', pattern: 'client/docs.json' },
  { method: 'POST', pattern: 'client/job/apply' },
  { method: 'GET', pattern: 'client/jobs' },
  { method: 'GET', pattern: 'client/jobs/:id' },
  { method: 'GET', pattern: 'client/location/:id' },
  { method: 'GET', pattern: 'company' },
  { method: 'GET', pattern: 'company/ai-score-weights' },
  { method: 'DELETE', pattern: 'company/:companyId/leave-company' },
  { method: 'GET', pattern: 'company/all-companies' },
  { method: 'GET', pattern: 'company/benefit' },
  { method: 'GET', pattern: 'company/company-products' },
  { method: 'GET', pattern: 'company/credit-breakdown' },
  { method: 'GET', pattern: 'company/credits' },
  { method: 'GET', pattern: 'company/licence' },
  { method: 'PUT', pattern: 'company/licence/:companyLicenceId/accept' },
  { method: 'GET', pattern: 'company/private/company-products' },
  { method: 'POST', pattern: 'company/reset-data' },
  { method: 'GET', pattern: 'company/transactions' },
  { method: 'GET', pattern: 'crons/:cron' },
  { method: 'GET', pattern: 'custom-fields' },
  { method: 'POST', pattern: 'custom-fields' },
  { method: 'DELETE', pattern: 'custom-fields/:id' },
  { method: 'PUT', pattern: 'custom-fields/:id' },
  { method: 'GET', pattern: 'custom-fields/short-code' },
  { method: 'GET', pattern: 'dashboard' },
  { method: 'GET', pattern: 'dashboard/app-inflow' },
  { method: 'GET', pattern: 'dashboard/app-source' },
  { method: 'GET', pattern: 'dashboard/fill-rate' },
  { method: 'GET', pattern: 'dashboard/live-jobs' },
  { method: 'GET', pattern: 'dashboard/pipeline-graph' },
  { method: 'GET', pattern: 'dashboard/upcoming-events' },
  { method: 'GET', pattern: 'decline-reasons' },
  { method: 'GET', pattern: 'departments' },
  { method: 'POST', pattern: 'documents/app-non-sign-documents' },
  { method: 'POST', pattern: 'documents/app-non-sign-documents/presigned-upload' },
  { method: 'POST', pattern: 'documents/app-non-sign-documents/send' },
  { method: 'POST', pattern: 'documents/app-sign-document' },
  { method: 'GET', pattern: 'documents/app-sign-documents' },
  { method: 'DELETE', pattern: 'documents/app-sign-documents/:id' },
  { method: 'GET', pattern: 'documents/app-sign-documents/:id/details' },
  { method: 'POST', pattern: 'documents/app-sign-documents/send' },
  { method: 'GET', pattern: 'documents/application/offers/:appid' },
  { method: 'GET', pattern: 'documents/download/:appDocId' },
  { method: 'POST', pattern: 'documents/file/extraction' },
  { method: 'PUT', pattern: 'documents/re-attempt/:id' },
  { method: 'POST', pattern: 'documents/webhook/signed' },
  { method: 'GET', pattern: 'download/media/:id' },
  { method: 'GET', pattern: 'download/shared/media/:id' },
  { method: 'GET', pattern: 'e-signee' },
  { method: 'GET', pattern: 'e-signee/:id' },
  { method: 'GET', pattern: 'feeds/adzuna/:companyId' },
  { method: 'GET', pattern: 'feeds/career-site' },
  { method: 'GET', pattern: 'feeds/indeed/:companyId' },
  { method: 'GET', pattern: 'feeds/indeed/v2/:companyId' },
  { method: 'GET', pattern: 'feeds/internal' },
  { method: 'GET', pattern: 'feeds/job' },
  { method: 'GET', pattern: 'feeds/reddot' },
  { method: 'GET', pattern: 'feeds/reed' },
  { method: 'GET', pattern: 'feeds/talent' },
  { method: 'GET', pattern: 'forms/app-questionnaire-forms/candidate/:candidateId' },
  { method: 'GET', pattern: 'forms/app-questionnaire-forms/candidate/:candidateId/by-team' },
  { method: 'GET', pattern: 'forms/application-forms' },
  { method: 'GET', pattern: 'forms/application-forms/candidate/:id' },
  { method: 'GET', pattern: 'forms/dei/form-questions' },
  { method: 'GET', pattern: 'forms/dei/form-status-graph' },
  { method: 'GET', pattern: 'forms/dei/pool-diversity-graph' },
  { method: 'POST', pattern: 'google/auth/claim-grant' },
  { method: 'POST', pattern: 'google/auth/refresh-auth' },
  { method: 'GET', pattern: 'google/auth/signin' },
  { method: 'GET', pattern: 'hiring-plans/:id' },
  { method: 'GET', pattern: 'hiring-plans/app/:appId' },
  { method: 'POST', pattern: 'hiring-plans/expiry/notify' },
  { method: 'GET', pattern: 'hiring-plans/job/:jhpId' },
  { method: 'GET', pattern: 'hiring-plans/job/:jobId/jhp' },
  { method: 'POST', pattern: 'hiring-plans/pre-signed-doc' },
  { method: 'GET', pattern: 'interviews/public' },
  { method: 'POST', pattern: 'interviews/public' },
  { method: 'GET', pattern: 'interviews/reminder' },
  { method: 'GET', pattern: 'interviews/slots/public' },
  { method: 'GET', pattern: 'job-automations/jobs/:jobId/hiring-stages-events' },
  { method: 'GET', pattern: 'job-automations/stage-events/exit' },
  { method: 'GET', pattern: 'job-category' },
  { method: 'GET', pattern: 'job-jhp-details/:jobId/:jhpId' },
  { method: 'GET', pattern: 'job-template/:jobId' },
  { method: 'GET', pattern: 'job/entity-details' },
  { method: 'POST', pattern: 'jobOffers' },
  { method: 'POST', pattern: 'jobOffers/invitation' },
  { method: 'POST', pattern: 'jobOffers/preSignedDoc' },
  { method: 'GET', pattern: 'jobOffers/public' },
  { method: 'POST', pattern: 'jobOffers/public' },
  { method: 'POST', pattern: 'jobOffers/sms-invitation' },
  { method: 'GET', pattern: 'jobs/account/follow/:jobId/:jobHiringPlanId' },
  { method: 'PUT', pattern: 'jobs/account/follow/:jobId/:jobHiringPlanId' },
  { method: 'GET', pattern: 'jobs/:jobId/:jhpId/campaigns' },
  { method: 'PUT', pattern: 'jobs/:jobId/archive' },
  { method: 'GET', pattern: 'jobs/:jobId/campaigns/:campaignId' },
  { method: 'GET', pattern: 'jobs/:jobId/hiring-stages' },
  { method: 'POST', pattern: 'jobs/:jobId/campaigns/:campaignId/closed' },
  { method: 'PUT', pattern: 'jobs/:jobId/campaigns/:campaignId/finalize' },
  { method: 'PUT', pattern: 'jobs/:jobId/close' },
  { method: 'POST', pattern: 'jobs/:jobId/duplicate' },
  { method: 'GET', pattern: 'jobs/:jobId/postings' },
  { method: 'GET', pattern: 'jobs/:jobId/skills' },
  { method: 'POST', pattern: 'jobs/:jobId/skills' },
  { method: 'DELETE', pattern: 'jobs/:jobId/skills/:skillId' },
  { method: 'GET', pattern: 'jobs/campaigns/:campaignId' },
  { method: 'GET', pattern: 'jobs/dashboard/stats/:jobId' },
  { method: 'GET', pattern: 'jobs/data-fields/:jobId/job-id' },
  { method: 'PUT', pattern: 'jobs/data-fields/:jobId/job-id' },
  { method: 'GET', pattern: 'jobs/data-fields/company' },
  { method: 'POST', pattern: 'jobs/joboffers/createOffer' },
  { method: 'GET', pattern: 'jobs/joboffers/getofferletter' },
  { method: 'POST', pattern: 'jobs/joboffers/preSignedofferattachment' },
  { method: 'POST', pattern: 'jobs/joboffers/sendInvite' },
  { method: 'GET', pattern: 'jobs/notes' },
  { method: 'GET', pattern: 'jobs/notes/:id/:jobId' },
  { method: 'GET', pattern: 'jobs/summary/:jobId/:jhpId' },
  { method: 'GET', pattern: 'locations' },
  { method: 'POST', pattern: 'locations' },
  { method: 'DELETE', pattern: 'locations/:id' },
  { method: 'PUT', pattern: 'locations/:id' },
  { method: 'POST', pattern: 'locations/company' },
  { method: 'GET', pattern: 'media' },
  { method: 'POST', pattern: 'media' },
  { method: 'PUT', pattern: 'media' },
  { method: 'DELETE', pattern: 'media/:id' },
  { method: 'GET', pattern: 'media/:id' },
  { method: 'POST', pattern: 'ms/auth/claim-grant' },
  { method: 'POST', pattern: 'ms/auth/refresh-auth' },
  { method: 'GET', pattern: 'ms/auth/signin' },
  { method: 'GET', pattern: 'notifications' },
  { method: 'DELETE', pattern: 'notifications/delete/:id' },
  { method: 'DELETE', pattern: 'notifications/email' },
  { method: 'GET', pattern: 'notifications/email' },
  { method: 'POST', pattern: 'notifications/email' },
  { method: 'PUT', pattern: 'notifications/mark-read' },
  { method: 'PUT', pattern: 'notifications/read/:id' },
  { method: 'GET', pattern: 'notifications/settings' },
  { method: 'PUT', pattern: 'notifications/settings/:id' },
  { method: 'GET', pattern: 'out-of-office' },
  { method: 'POST', pattern: 'out-of-office' },
  { method: 'DELETE', pattern: 'out-of-office/:id' },
  { method: 'PUT', pattern: 'out-of-office/:id' },
  { method: 'POST', pattern: 'pipe/media' },
  { method: 'POST', pattern: 'post-create/media' },
  { method: 'POST', pattern: 'pre-create/media' },
  { method: 'GET', pattern: 'products' },
  { method: 'POST', pattern: 'public/applicant-employment-agreement/accepted-rejected' },
  { method: 'GET', pattern: 'public/applicant-employment-agreement/details' },
  { method: 'GET', pattern: 'public/applicant-employment-agreement/signed/hook' },
  { method: 'POST', pattern: 'public/applicant-employment-agreement/signed/hook' },
  { method: 'POST', pattern: 'public/apply/adzuna' },
  { method: 'GET', pattern: 'public/apply/desposition/:jbAccessKey' },
  { method: 'POST', pattern: 'public/apply/monster' },
  { method: 'POST', pattern: 'public/apply/reed/:refId' },
  { method: 'GET', pattern: 'public/apply/tj-resp-url/:refId' },
  { method: 'POST', pattern: 'public/apply/total-job' },
  { method: 'POST', pattern: 'public/candidates' },
  { method: 'POST', pattern: 'public/candidates/apply' },
  { method: 'POST', pattern: 'public/candidates/indeed_organic/:jobSlug' },
  { method: 'POST', pattern: 'public/candidates/indeed_sponsored/:jobSlug' },
  { method: 'POST', pattern: 'public/candidates/job_look_up' },
  { method: 'GET', pattern: 'public/candidates/mail/details' },
  { method: 'POST', pattern: 'public/candidates/presigned-doc' },
  { method: 'POST', pattern: 'public/candidates/presigned-video-profile-doc' },
  { method: 'POST', pattern: 'public/candidates/talent/:jobSlug' },
  { method: 'POST', pattern: 'public/candidates/unsubscribe' },
  { method: 'GET', pattern: 'public/careersite/company/jobs' },
  { method: 'GET', pattern: 'public/careersite/company/page/:link' },
  { method: 'GET', pattern: 'public/careersite/company/pages' },
  { method: 'GET', pattern: 'public/careersite/company/subsidiaries' },
  { method: 'GET', pattern: 'public/company/details' },
  { method: 'POST', pattern: 'public/documents/app-non-sign-documents/accepted-rejected' },
  { method: 'GET', pattern: 'public/documents/app-non-sign-documents/details' },
  { method: 'GET', pattern: 'public/forms/application-form' },
  { method: 'POST', pattern: 'public/forms/application-form' },
  { method: 'GET', pattern: 'public/forms/dei/application-form' },
  { method: 'POST', pattern: 'public/forms/dei/application-form' },
  { method: 'POST', pattern: 'public/forms/dei/declined' },
  { method: 'POST', pattern: 'public/forms/presigned' },
  { method: 'GET', pattern: 'public/forms/static/file' },
  { method: 'GET', pattern: 'public/forms/static/file' },
  { method: 'GET', pattern: 'public/job-category' },
  { method: 'GET', pattern: 'public/jobs' },
  { method: 'GET', pattern: 'public/jobs.xml' },
  { method: 'GET', pattern: 'public/jobs/:campaignId' },
  { method: 'GET', pattern: 'public/jobs/posting/:postingId/details' },
  { method: 'POST', pattern: 'public/jobs/status' },
  { method: 'POST', pattern: 'public/video-profiles/save-answer' },
  { method: 'GET', pattern: 'public/video-profiles/vp-details/questionnaire' },
  { method: 'PUT', pattern: 'public/video-profiles/vp-form/start-session' },
  { method: 'PUT', pattern: 'public/video-profiles/vp-form/submit' },
  { method: 'GET', pattern: 'ref-check/timeline' },
  { method: 'GET', pattern: 'roles' },
  { method: 'GET', pattern: 'shortcodes' },
  { method: 'POST', pattern: 'shortcodes/check' },
  { method: 'GET', pattern: 'signable/templates' },
  { method: 'GET', pattern: 'signed/media' },
  { method: 'GET', pattern: 'sitemap.xml' },
  { method: 'GET', pattern: 'skills' },
  { method: 'GET', pattern: 'stages/company/default' },
  { method: 'PUT', pattern: 'stages/job-hiring/:jobId/default' },
  { method: 'PUT', pattern: 'stages/job-hiring/:jobId/pipeline-template/:templateId' },
  { method: 'PUT', pattern: 'stages/job-hiring/switch' },
  { method: 'PUT', pattern: 'stages/jobs-hiring-stage/:jobHiringStageId/trigger' },
  { method: 'GET', pattern: 'stages/jobs-hiring-stage/trigger/:jobHiringStageId/details' },
  { method: 'DELETE', pattern: 'stages/jobs-hiring-stage/trigger/:triggerId' },
  { method: 'GET', pattern: 'stages/pipeline-templates' },
  { method: 'POST', pattern: 'stages/pipeline-templates/job-hiring' },
  { method: 'PUT', pattern: 'stages/pipeline-templates/job-hiring' },
  { method: 'GET', pattern: 'stages/pipeline-templates/list' },
  { method: 'GET', pattern: 'stages/stage-groups' },
  { method: 'GET', pattern: 'static/file/:token' },
  { method: 'GET', pattern: 'tags' },
  { method: 'POST', pattern: 'tags/candidate' },
  { method: 'DELETE', pattern: 'tags/candidate/:candTagId' },
  { method: 'GET', pattern: 'templates' },
  { method: 'GET', pattern: 'templates/:id' },
  { method: 'GET', pattern: 'templates/email/gdpr' },
  { method: 'POST', pattern: 'templates/preSignedTemplateAttachment' },
  { method: 'GET', pattern: 'timeline' },
  { method: 'GET', pattern: 'timeline-tpls' },
  { method: 'GET', pattern: 'timeline/jobs' },
  { method: 'GET', pattern: 'tpl-automations/stage-events/exit' },
  { method: 'GET', pattern: 'tpl-automations/tpl/:templateId/hiring-stages-events' },
  { method: 'GET', pattern: 'video-profiles/application/:id' },
  { method: 'POST', pattern: 'video-profiles/vp-resend/:id' },
  { method: 'POST', pattern: 'video-profiles/vp-reset/:id' },
  { method: 'GET', pattern: 'zapier/auth' },
  { method: 'GET', pattern: 'zapier/polling-sample' },
  { method: 'POST', pattern: 'zapier/subscribe' },
  { method: 'DELETE', pattern: 'zapier/unSubscribe' },
  { method: 'GET', pattern: 'backoffice/auth/callback' },
  { method: 'DELETE', pattern: 'backoffice/auth/disable-mfa' },
  { method: 'POST', pattern: 'backoffice/auth/force/log-in' },
  { method: 'GET', pattern: 'backoffice/auth/signin' },
  { method: 'DELETE', pattern: 'backoffice/candidates/account/gdpr' },
  { method: 'GET', pattern: 'backoffice/careersite/components' },
  { method: 'POST', pattern: 'backoffice/careersite/components' },
  { method: 'DELETE', pattern: 'backoffice/careersite/components/:id' },
  { method: 'GET', pattern: 'backoffice/careersite/components/:id' },
  { method: 'PUT', pattern: 'backoffice/careersite/components/:id' },
  { method: 'GET', pattern: 'backoffice/companies' },
  { method: 'POST', pattern: 'backoffice/companies' },
  { method: 'DELETE', pattern: 'backoffice/companies/:id' },
  { method: 'GET', pattern: 'backoffice/companies/:id' },
  { method: 'PUT', pattern: 'backoffice/companies/:id' },
  { method: 'GET', pattern: 'backoffice/companies/:id/accounts' },
  { method: 'POST', pattern: 'backoffice/companies/:id/accounts' },
  { method: 'DELETE', pattern: 'backoffice/companies/:id/accounts/:accountId' },
  { method: 'PUT', pattern: 'backoffice/companies/:id/accounts/:accountId' },
  { method: 'GET', pattern: 'backoffice/companies/:id/campaigns' },
  { method: 'GET', pattern: 'backoffice/companies/:id/jobs' },
  { method: 'DELETE', pattern: 'backoffice/companies/:id/jobs/:jobId' },
  { method: 'GET', pattern: 'backoffice/companies/:id/jobs/:jobId' },
  { method: 'POST', pattern: 'backoffice/companies/:id/licences' },
  { method: 'PUT', pattern: 'backoffice/companies/:id/licences/:companyLicenceId' },
  { method: 'GET', pattern: 'backoffice/companies/:id/licences/active' },
  { method: 'GET', pattern: 'backoffice/companies/:id/licences/new' },
  { method: 'POST', pattern: 'backoffice/companies/:id/preSignedLicenceAgreement' },
  { method: 'POST', pattern: 'backoffice/companies/:id/preSignedProfileImage' },
  { method: 'GET', pattern: 'backoffice/companies/:id/products' },
  { method: 'POST', pattern: 'backoffice/companies/:id/products' },
  { method: 'PUT', pattern: 'backoffice/companies/:id/products' },
  { method: 'DELETE', pattern: 'backoffice/companies/:id/products/:productId' },
  { method: 'GET', pattern: 'backoffice/companies/:id/settings' },
  { method: 'DELETE', pattern: 'backoffice/companies/:id/settings/:settingsId' },
  { method: 'POST', pattern: 'backoffice/companies/:id/settings/:settingsId' },
  { method: 'GET', pattern: 'backoffice/companies/:id/stats' },
  { method: 'POST', pattern: 'backoffice/companies/:id/statusChange' },
  { method: 'GET', pattern: 'backoffice/companies/:superCompanyId/child-companies' },
  { method: 'GET', pattern: 'backoffice/companies/accountRoles' },
  { method: 'GET', pattern: 'backoffice/companies/credits' },
  { method: 'POST', pattern: 'backoffice/companies/credits' },
  { method: 'POST', pattern: 'backoffice/companies/credits-deduction' },
  { method: 'GET', pattern: 'backoffice/companies/jobCategory' },
  { method: 'PUT', pattern: 'backoffice/companies/modify-subsidiary/:companyId' },
  { method: 'PUT', pattern: 'backoffice/companies/revert-transaction' },
  { method: 'GET', pattern: 'backoffice/companies/settings' },
  { method: 'POST', pattern: 'backoffice/companies/settings' },
  { method: 'PUT', pattern: 'backoffice/companies/settings' },
  { method: 'DELETE', pattern: 'backoffice/companies/settings/:settingsId' },
  { method: 'GET', pattern: 'backoffice/companies/transactions' },
  { method: 'POST', pattern: 'backoffice/company/providers' },
  { method: 'GET', pattern: 'backoffice/company/providers/:compId' },
  { method: 'DELETE', pattern: 'backoffice/company/providers/:compId/:compProviderId' },
  { method: 'GET', pattern: 'backoffice/copy-write' },
  { method: 'POST', pattern: 'backoffice/copy-write' },
  { method: 'GET', pattern: 'backoffice/copy-write/:jobId' },
  { method: 'GET', pattern: 'backoffice/copy-write/status' },
  { method: 'POST', pattern: 'backoffice/data-anonymisation' },
  { method: 'GET', pattern: 'backoffice/jobs' },
  { method: 'PUT', pattern: 'backoffice/jobs/:id/change-copywrite-status' },
  { method: 'GET', pattern: 'backoffice/jobs/campaigns' },
  { method: 'GET', pattern: 'backoffice/jobs/campaigns/:campaignId' },
  { method: 'POST', pattern: 'backoffice/jobs/campaigns/:campaignId/closed' },
  { method: 'GET', pattern: 'backoffice/jobs/campaigns/:campaignId/job-postings' },
  { method: 'GET', pattern: 'backoffice/jobs/campaigns/:campaignId/occurrence' },
  { method: 'GET', pattern: 'backoffice/jobs/campaigns/:campaignId/occurrence' },
  { method: 'GET', pattern: 'backoffice/jobs/campaigns/job-postings/per-occurrence' },
  { method: 'GET', pattern: 'backoffice/jobs/data-fields' },
  { method: 'POST', pattern: 'backoffice/jobs/data-fields' },
  { method: 'DELETE', pattern: 'backoffice/jobs/data-fields/:id' },
  { method: 'GET', pattern: 'backoffice/jobs/data-fields/:id' },
  { method: 'PUT', pattern: 'backoffice/jobs/data-fields/:id' },
  { method: 'POST', pattern: 'backoffice/jobs/job-board-posting' },
  { method: 'GET', pattern: 'backoffice/jobs/job-board-posting-summary/:advertId' },
  { method: 'PUT', pattern: 'backoffice/jobs/job-board-re-posting' },
  { method: 'GET', pattern: 'backoffice/jobs/job-posting/:jobPostingId' },
  { method: 'GET', pattern: 'backoffice/jobs/list' },
  { method: 'GET', pattern: 'backoffice/jobs/postings' },
  { method: 'GET', pattern: 'backoffice/jobs/postings/:postingId/advertise/manual' },
  { method: 'GET', pattern: 'backoffice/jobs/postings/:postingId/broadbean-status' },
  { method: 'GET', pattern: 'backoffice/jobs/postings/:postingId/cancel' },
  { method: 'GET', pattern: 'backoffice/jobs/postings/:postingId/mark-advertised' },
  { method: 'PUT', pattern: 'backoffice/jobs/postings/mark-advertised' },
  { method: 'GET', pattern: 'backoffice/licences' },
  { method: 'POST', pattern: 'backoffice/licences' },
  { method: 'DELETE', pattern: 'backoffice/licences/:id' },
  { method: 'GET', pattern: 'backoffice/licences/:id' },
  { method: 'PUT', pattern: 'backoffice/licences/:id' },
  { method: 'GET', pattern: 'backoffice/licences/types' },
  { method: 'POST', pattern: 'backoffice/migration/candidates' },
  { method: 'POST', pattern: 'backoffice/migration/career-site/version-backfill' },
  { method: 'GET', pattern: 'backoffice/migration/es/advert' },
  { method: 'POST', pattern: 'backoffice/migration/gdpr-tpl' },
  { method: 'POST', pattern: 'backoffice/migration/jobs' },
  { method: 'GET', pattern: 'backoffice/migration/jr' },
  { method: 'POST', pattern: 'backoffice/migration/std/forms' },
  { method: 'POST', pattern: 'backoffice/migration/std/templates' },
  { method: 'POST', pattern: 'backoffice/migration/users' },
  { method: 'GET', pattern: 'backoffice/multi-poster/company/job-board/products/:compId/:jbAccessKey' },
  { method: 'POST', pattern: 'backoffice/multi-poster/company/job-boards' },
  { method: 'GET', pattern: 'backoffice/multi-poster/company/job-boards/:compId' },
  { method: 'DELETE', pattern: 'backoffice/multi-poster/company/job-boards/:compId/:compJobBoardId' },
  { method: 'GET', pattern: 'backoffice/multi-poster/job-boards' },
  { method: 'GET', pattern: 'backoffice/on-boarding' },
  { method: 'PUT', pattern: 'backoffice/on-boarding/hand-off-to-cs/:compId' },
  { method: 'GET', pattern: 'backoffice/on-boarding/user/:compId' },
  { method: 'GET', pattern: 'backoffice/on-boarding/wizard-details/:compId' },
  { method: 'POST', pattern: 'backoffice/permissions' },
  { method: 'DELETE', pattern: 'backoffice/permissions/:id' },
  { method: 'GET', pattern: 'backoffice/products' },
  { method: 'POST', pattern: 'backoffice/products' },
  { method: 'PUT', pattern: 'backoffice/products' },
  { method: 'DELETE', pattern: 'backoffice/products/:id' },
  { method: 'GET', pattern: 'backoffice/products/:id' },
  { method: 'GET', pattern: 'backoffice/products/jobboards' },
  { method: 'GET', pattern: 'backoffice/reports/admin-overview' },
  { method: 'GET', pattern: 'backoffice/reports/campaigns/campaign-analysis' },
  { method: 'GET', pattern: 'backoffice/reports/campaigns/campaign-performance' },
  { method: 'GET', pattern: 'backoffice/reports/campaigns/candidate-analysis' },
  { method: 'GET', pattern: 'backoffice/reports/candidates/candidate-analysis' },
  { method: 'GET', pattern: 'backoffice/reports/candidates/location' },
  { method: 'GET', pattern: 'backoffice/reports/candidates/rejection-reason' },
  { method: 'GET', pattern: 'backoffice/reports/company-accounts' },
  { method: 'GET', pattern: 'backoffice/reports/company-campaigns' },
  { method: 'GET', pattern: 'backoffice/reports/company-decline-reasons' },
  { method: 'GET', pattern: 'backoffice/reports/company-depts' },
  { method: 'GET', pattern: 'backoffice/reports/company-job-categories' },
  { method: 'GET', pattern: 'backoffice/reports/company-products' },
  { method: 'GET', pattern: 'backoffice/reports/credit-consumption' },
  { method: 'GET', pattern: 'backoffice/reports/customer-growth' },
  { method: 'GET', pattern: 'backoffice/reports/download' },
  { method: 'GET', pattern: 'backoffice/reports/download/applicant-data' },
  { method: 'GET', pattern: 'backoffice/reports/download/job-data' },
  { method: 'GET', pattern: 'backoffice/reports/feature/automation-trend-graph' },
  { method: 'GET', pattern: 'backoffice/reports/feature/popular-automation-graph' },
  { method: 'GET', pattern: 'backoffice/reports/feature/popular-features-graph' },
  { method: 'GET', pattern: 'backoffice/reports/feature/top-metrics/adoption' },
  { method: 'GET', pattern: 'backoffice/reports/get-jobs-title' },
  { method: 'GET', pattern: 'backoffice/reports/jobs/copywritten' },
  { method: 'GET', pattern: 'backoffice/reports/jobs/hiring-trend' },
  { method: 'GET', pattern: 'backoffice/reports/jobs/interviews-scheduled' },
  { method: 'GET', pattern: 'backoffice/reports/jobs/job-analysis' },
  { method: 'GET', pattern: 'backoffice/reports/key-performance-metrics' },
  { method: 'GET', pattern: 'backoffice/reports/key-performance-metrics/candidate-pool' },
  { method: 'GET', pattern: 'backoffice/reports/keymetrics/customers-and-feature' },
  { method: 'GET', pattern: 'backoffice/reports/onboarding/avg-stagetime-graph' },
  { method: 'GET', pattern: 'backoffice/reports/onboarding/customer-status' },
  { method: 'GET', pattern: 'backoffice/reports/onboarding/status-graph' },
  { method: 'GET', pattern: 'backoffice/reports/onboarding/top-metrics' },
  { method: 'GET', pattern: 'backoffice/reports/onboarding/trend-graph' },
  { method: 'GET', pattern: 'backoffice/reports/overview/campaign-performance' },
  { method: 'GET', pattern: 'backoffice/reports/overview/hiring-efficiency' },
  { method: 'GET', pattern: 'backoffice/reports/overview/hp-progression' },
  { method: 'GET', pattern: 'backoffice/reports/thirdparty/automation-trend-graph' },
  { method: 'GET', pattern: 'backoffice/reports/thirdparty/integration-breakdown-graph' },
  { method: 'GET', pattern: 'backoffice/reports/thirdparty/integration-usage-graph' },
  { method: 'GET', pattern: 'backoffice/reports/thirdparty/top-customer-graph' },
  { method: 'GET', pattern: 'backoffice/reports/thirdparty/top-metrics/integrations' },
  { method: 'GET', pattern: 'backoffice/reports/top-spenders' },
];


export const ROUTE_PERMISSION_RULES = ROUTE_PERMISSION_GROUPS.flatMap(
  ({ permission, permissions, entries }) => {
    const normalizedPermissions = permissions || (permission ? [permission] : []);
    return entries.flatMap(({ methods, pattern }) =>
      methods.map((method) => ({
        method,
        pattern,
        permissions: normalizedPermissions,
        permission: normalizedPermissions[0],
      })),
    );
  },
);

/**
 * Routes that still require the mapped permission but suppress the
 * permission-required modal when access is denied (optional / handled UI).
 */
export const ROUTES_SILENT_PERMISSION_DENY = [
  { method: 'GET', pattern: 'social-media/posting/products' },
];
